import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  COMPOUND_FORBIDDEN_LEAF_CHARACTERISTICS,
  CompoundNarySegment,
  CompoundSegmentCondition,
  CompoundSegmentGroup,
  CompoundSegmentLeaf,
  SegmentableCheckoutCharacteristic,
  SegmentSchema,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  getDefaultSegments,
  NarySegmentCharacteristic,
  segmentableCharacteristicLabel,
} from "@redotech/redo-conversion-core-common/checkouts/segments";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import {
  RcoNarySegmentEdge,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { FlowInProgress } from "@redotech/redo-flows-common/flow-in-progress";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { t, useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ConditionConnector } from "@redotech/redo-web-marketing/marketing/segments/condition-connector";
import { blockBorderProps } from "@redotech/redo-web-marketing/marketing/segments/segment-builder-editor";
import { SegmentConjunctionSelect } from "@redotech/redo-web-marketing/marketing/segments/segment-conjunction-select";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ObjectId } from "bson";
import { Fragment, memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";
import { deleteElementAndAllChildren } from "../../../utils/delete-element";
import { SegmentNameInput } from "../../common/segment-name-input";
import { SegmentTypeInspectorContent } from "../segment-type-editor";

type LeafCharacteristic = Exclude<
  SegmentableCheckoutCharacteristic,
  NarySegmentCharacteristic
>;

function compoundSegment(
  conditions: CompoundSegmentCondition[],
): CompoundNarySegment {
  return {
    segmentableCharacteristic: SegmentableCheckoutCharacteristic.COMPOUND,
    conditions,
  };
}

function GROUPS_CONJUNCTION_TEXT() {
  return t("of these condition groups must match");
}
function LEAVES_CONJUNCTION_TEXT() {
  return t("of these conditions must match");
}

function defaultConditionName(index: number): string {
  return `Condition ${index + 1}`;
}

export const CompoundInspectorContent = memo(function CompoundInspectorContent({
  segment,
}: {
  segment: CompoundNarySegment;
}) {
  const { t } = useTranslation();
  return (
    <Flex dir="column" gap="lg" w="full">
      {segment.conditions.map((condition, index) => (
        <Flex
          {...blockBorderProps}
          align="flex-start"
          dir="column"
          gap="sm"
          key={condition.edgeId}
          p="md"
          w="full"
        >
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {condition.name || defaultConditionName(index)}
          </Text>
          {condition.groups.length > 1 && (
            <SegmentConjunctionSelect
              mode={condition.conjunction}
              readonly
              textAfterConjunction={GROUPS_CONJUNCTION_TEXT()}
            />
          )}
          {condition.groups.map((group, groupIndex) => (
            <Fragment key={groupIndex}>
              {groupIndex > 0 && (
                <ConditionConnector conjunction={condition.conjunction} />
              )}
              <Flex
                {...blockBorderProps}
                align="flex-start"
                dir="column"
                gap="xs"
                p="sm"
                w="full"
              >
                {group.leaves.length > 1 && (
                  <SegmentConjunctionSelect
                    mode={group.conjunction}
                    readonly
                    textAfterConjunction={LEAVES_CONJUNCTION_TEXT()}
                  />
                )}
                {group.leaves.map((leaf, leafIndex) => (
                  <Fragment key={leafIndex}>
                    {leafIndex > 0 && (
                      <ConditionConnector conjunction={group.conjunction} />
                    )}
                    {/* Column + full width: leaf inspectors emit several
                        stacked blocks (a label, then a wrap-row of chips for
                        list-valued segments like countries/tags). A row here
                        lays those blocks out side-by-side and overflows on
                        large inputs. */}
                    <Flex align="flex-start" dir="column" gap="xs" w="full">
                      {leaf.negate && (
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          textColor="primary"
                        >
                          {t("NOT")}
                        </Text>
                      )}
                      <SegmentTypeInspectorContent
                        readonly
                        setStep={() => {}}
                        step={{
                          type: StepType.RCO_SEGMENT,
                          id: `${condition.edgeId}-${groupIndex}-${leafIndex}`,
                          segment: leaf.segment,
                        }}
                      />
                    </Flex>
                  </Fragment>
                ))}
              </Flex>
            </Fragment>
          ))}
        </Flex>
      ))}
    </Flex>
  );
});

export const EditableCompoundInspectorContent = memo(
  function EditableCompoundInspectorContent({
    step,
    setStep,
    setAutomation,
  }: {
    step: PartialWithRequiredType<RcoSegmentStep>;
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
    setAutomation?: React.Dispatch<React.SetStateAction<FlowInProgress | null>>;
  }) {
    const { t } = useTranslation();
    const team = useRequiredContext(TeamContext);
    const defaultSegments = useMemo(() => getDefaultSegments(team), [team]);

    const segment = step.segment as CompoundNarySegment | undefined;
    const conditions = useMemo(
      () => segment?.conditions ?? [],
      [segment?.conditions],
    );

    const leafCharacteristicOptions: TextRedoListItem<LeafCharacteristic>[] =
      useMemo(() => {
        // Keys of getDefaultSegments are exactly the non-n-ary characteristics.
        const characteristics = Object.keys(
          defaultSegments,
        ) as LeafCharacteristic[];
        return (
          characteristics
            .filter(
              (characteristic) =>
                !(
                  COMPOUND_FORBIDDEN_LEAF_CHARACTERISTICS as readonly SegmentableCheckoutCharacteristic[]
                ).includes(characteristic),
            )
            // Inside a compound, DELIVERY_LOCATION_RULES supersedes the legacy
            // binary DELIVERY_LOCATION (it supports arbitrary per-country
            // provinces, matching the n-ary delivery-location segment). Offer
            // only the rules variant — both share the "Delivery location" label.
            .filter(
              (characteristic) =>
                characteristic !==
                SegmentableCheckoutCharacteristic.DELIVERY_LOCATION,
            )
            .filter(
              (characteristic) =>
                !!team.settings.landedCost?.enabled ||
                characteristic !==
                  SegmentableCheckoutCharacteristic.LCG_ELIGIBLE_SHIPMENT,
            )
            .map((characteristic) => ({
              type: "text" as const,
              id: characteristic,
              value: characteristic,
              text: segmentableCharacteristicLabel[characteristic],
            }))
            .sort((a, b) => a.text.localeCompare(b.text))
        );
      }, [defaultSegments, team.settings.landedCost?.enabled]);

    const updateConditions = useHandler(
      (updated: CompoundSegmentCondition[]) => {
        setStep({ ...step, segment: compoundSegment(updated) });
      },
    );

    const updateCondition = useHandler(
      (
        edgeId: string,
        updater: (
          condition: CompoundSegmentCondition,
        ) => CompoundSegmentCondition,
      ) => {
        updateConditions(
          conditions.map((condition) =>
            condition.edgeId === edgeId ? updater(condition) : condition,
          ),
        );
      },
    );

    const handleAddCondition = useHandler(() => {
      const newEdgeId = new ObjectId().toString();
      const newEdge: RcoNarySegmentEdge = { edgeId: newEdgeId, nextId: "" };
      const newCondition: CompoundSegmentCondition = {
        edgeId: newEdgeId,
        conjunction: ConjunctionMode.OR,
        groups: [defaultGroup(defaultSegments)],
      };
      setStep({
        ...step,
        segment: compoundSegment([...conditions, newCondition]),
        edges: [...(step.edges ?? []), newEdge],
      });
    });

    const handleRemoveCondition = useHandler((edgeId: string) => {
      const edgeToRemove = (step.edges ?? []).find((e) => e.edgeId === edgeId);
      setStep({
        ...step,
        segment: compoundSegment(
          conditions.filter((condition) => condition.edgeId !== edgeId),
        ),
        edges: (step.edges ?? []).filter((e) => e.edgeId !== edgeId),
      });

      if (setAutomation && edgeToRemove?.nextId) {
        setAutomation((prev) => {
          if (!prev || !edgeToRemove.nextId) {
            return prev;
          }
          return {
            ...prev,
            steps: deleteElementAndAllChildren(edgeToRemove.nextId, prev.steps),
          };
        });
      }
    });

    return (
      <Flex dir="column" gap="lg" w="full">
        {conditions.map((condition, index) => (
          <EditableCondition
            canDelete={conditions.length > 1}
            condition={condition}
            defaultSegments={defaultSegments}
            index={index}
            key={condition.edgeId}
            leafCharacteristicOptions={leafCharacteristicOptions}
            onRemove={handleRemoveCondition}
            onUpdate={updateCondition}
          />
        ))}
        <RedoButton
          hierarchy="secondary"
          IconLeading={PlusIcon}
          onClick={handleAddCondition}
          size="sm"
          text={t("Add condition")}
        />
      </Flex>
    );
  },
);

function defaultGroup(
  defaultSegments: ReturnType<typeof getDefaultSegments>,
): CompoundSegmentGroup {
  return {
    conjunction: ConjunctionMode.AND,
    leaves: [
      {
        negate: false,
        segment: defaultSegments[SegmentableCheckoutCharacteristic.CART_VALUE],
      },
    ],
  };
}

const EditableCondition = memo(function EditableCondition({
  condition,
  index,
  canDelete,
  onRemove,
  onUpdate,
  defaultSegments,
  leafCharacteristicOptions,
}: {
  condition: CompoundSegmentCondition;
  index: number;
  canDelete: boolean;
  onRemove: (edgeId: string) => void;
  onUpdate: (
    edgeId: string,
    updater: (condition: CompoundSegmentCondition) => CompoundSegmentCondition,
  ) => void;
  defaultSegments: ReturnType<typeof getDefaultSegments>;
  leafCharacteristicOptions: TextRedoListItem<LeafCharacteristic>[];
}) {
  const { t } = useTranslation();
  const setOuterConjunction = useHandler((conjunction: ConjunctionMode) => {
    onUpdate(condition.edgeId, (c) => ({ ...c, conjunction }));
  });

  const updateGroup = useHandler(
    (groupIndex: number, group: CompoundSegmentGroup) => {
      onUpdate(condition.edgeId, (c) => ({
        ...c,
        groups: c.groups.map((g, i) => (i === groupIndex ? group : g)),
      }));
    },
  );

  const removeGroup = useHandler((groupIndex: number) => {
    onUpdate(condition.edgeId, (c) => ({
      ...c,
      groups: c.groups.filter((_, i) => i !== groupIndex),
    }));
  });

  const addGroup = useHandler(() => {
    onUpdate(condition.edgeId, (c) => ({
      ...c,
      groups: [...c.groups, defaultGroup(defaultSegments)],
    }));
  });

  return (
    <Flex
      {...blockBorderProps}
      align="flex-start"
      dir="column"
      gap="sm"
      p="md"
      w="full"
    >
      <Flex align="center" gap="sm" justify="space-between" w="full">
        <Flex grow={1}>
          <SegmentNameInput
            onChange={(name) =>
              onUpdate(condition.edgeId, (c) => ({
                ...c,
                name: name ?? undefined,
              }))
            }
            placeholder={defaultConditionName(index)}
            value={condition.name ?? undefined}
          />
        </Flex>
        {canDelete && (
          <RedoButton
            aria-label={t("Remove")}
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={() => onRemove(condition.edgeId)}
            size="xs"
          />
        )}
      </Flex>

      {condition.groups.length > 1 && (
        <SegmentConjunctionSelect
          mode={condition.conjunction}
          setMode={setOuterConjunction}
          textAfterConjunction={GROUPS_CONJUNCTION_TEXT()}
        />
      )}

      {condition.groups.map((group, groupIndex) => (
        <Fragment key={groupIndex}>
          {groupIndex > 0 && (
            <ConditionConnector conjunction={condition.conjunction} />
          )}
          <EditableGroup
            canDelete={condition.groups.length > 1}
            defaultSegments={defaultSegments}
            group={group}
            leafCharacteristicOptions={leafCharacteristicOptions}
            onChange={(g) => updateGroup(groupIndex, g)}
            onRemove={() => removeGroup(groupIndex)}
          />
        </Fragment>
      ))}

      <RedoButton
        hierarchy="link"
        IconLeading={PlusIcon}
        onClick={addGroup}
        size="xs"
        text={t("Add group")}
      />
    </Flex>
  );
});

const EditableGroup = memo(function EditableGroup({
  group,
  onChange,
  onRemove,
  canDelete,
  defaultSegments,
  leafCharacteristicOptions,
}: {
  group: CompoundSegmentGroup;
  onChange: (group: CompoundSegmentGroup) => void;
  onRemove: () => void;
  canDelete: boolean;
  defaultSegments: ReturnType<typeof getDefaultSegments>;
  leafCharacteristicOptions: TextRedoListItem<LeafCharacteristic>[];
}) {
  const { t } = useTranslation();
  const setInnerConjunction = useHandler((conjunction: ConjunctionMode) => {
    onChange({ ...group, conjunction });
  });

  const updateLeaf = useHandler(
    (leafIndex: number, leaf: CompoundSegmentLeaf) => {
      onChange({
        ...group,
        leaves: group.leaves.map((l, i) => (i === leafIndex ? leaf : l)),
      });
    },
  );

  const removeLeaf = useHandler((leafIndex: number) => {
    onChange({
      ...group,
      leaves: group.leaves.filter((_, i) => i !== leafIndex),
    });
  });

  const addLeaf = useHandler(() => {
    onChange({
      ...group,
      leaves: [
        ...group.leaves,
        {
          negate: false,
          segment:
            defaultSegments[SegmentableCheckoutCharacteristic.CART_VALUE],
        },
      ],
    });
  });

  return (
    <Flex
      {...blockBorderProps}
      align="flex-start"
      dir="column"
      gap="sm"
      p="sm"
      w="full"
    >
      <Flex align="center" justify="space-between" w="full">
        {group.leaves.length > 1 ? (
          <SegmentConjunctionSelect
            mode={group.conjunction}
            setMode={setInnerConjunction}
            textAfterConjunction={LEAVES_CONJUNCTION_TEXT()}
          />
        ) : (
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Conditions")}
          </Text>
        )}
        {canDelete && (
          <RedoButton
            aria-label={t("Remove")}
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={onRemove}
            size="xs"
          />
        )}
      </Flex>
      {group.leaves.map((leaf, leafIndex) => (
        <Fragment key={leafIndex}>
          {leafIndex > 0 && (
            <ConditionConnector conjunction={group.conjunction} />
          )}
          <EditableLeaf
            canDelete={group.leaves.length > 1}
            defaultSegments={defaultSegments}
            leaf={leaf}
            leafCharacteristicOptions={leafCharacteristicOptions}
            onChange={(l) => updateLeaf(leafIndex, l)}
            onRemove={() => removeLeaf(leafIndex)}
          />
        </Fragment>
      ))}
      <RedoButton
        hierarchy="link"
        IconLeading={PlusIcon}
        onClick={addLeaf}
        size="xs"
        text={t("Add condition")}
      />
    </Flex>
  );
});

const EditableLeaf = memo(function EditableLeaf({
  leaf,
  onChange,
  onRemove,
  canDelete,
  defaultSegments,
  leafCharacteristicOptions,
}: {
  leaf: CompoundSegmentLeaf;
  onChange: (leaf: CompoundSegmentLeaf) => void;
  onRemove: () => void;
  canDelete: boolean;
  defaultSegments: ReturnType<typeof getDefaultSegments>;
  leafCharacteristicOptions: TextRedoListItem<LeafCharacteristic>[];
}) {
  const { t } = useTranslation();
  const characteristic = leaf.segment
    .segmentableCharacteristic as LeafCharacteristic;

  const setCharacteristic = useHandler((next: LeafCharacteristic) => {
    onChange({ ...leaf, segment: defaultSegments[next] });
  });

  const setNegate = useHandler((negate: boolean) => {
    onChange({ ...leaf, negate });
  });

  const setSegmentFromStep = useHandler(
    (newStep: PartialWithRequiredType<RcoSegmentStep>) => {
      if (!newStep.segment) {
        return;
      }
      onChange({ ...leaf, segment: SegmentSchema.parse(newStep.segment) });
    },
  );

  return (
    <Flex
      align="flex-start"
      borderColor="secondary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="sm"
      p="sm"
      w="full"
    >
      <Flex align="center" gap="sm" justify="space-between" w="full">
        <Flex grow={1}>
          <RedoSingleSelectDropdownInput<LeafCharacteristic>
            fitToAnchor={false}
            label={t("Segment by")}
            options={leafCharacteristicOptions}
            optionSelected={({ item }) => setCharacteristic(item.value)}
            selectedItem={leafCharacteristicOptions.find(
              (o) => o.value === characteristic,
            )}
          />
        </Flex>
        <RedoToggle label={t("Not")} setValue={setNegate} value={leaf.negate} />
        {canDelete && (
          <RedoButton
            aria-label={t("Remove")}
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={onRemove}
            size="xs"
          />
        )}
      </Flex>
      <SegmentTypeInspectorContent
        readonly={false}
        setStep={setSegmentFromStep}
        step={{
          type: StepType.RCO_SEGMENT,
          id: characteristic,
          segment: leaf.segment,
        }}
      />
    </Flex>
  );
});
